import {
  ENDPOINT_SERVICE_URL,
  ERROR_FIELD,
  HTTP,
  JSON_PG,
  JSON_PG_UPD,
  LOADENV,
  ROW_FIELDS_VALUE_PARAM,
  type QueryRecord,
  type StartEnv,
  type UpdateData,
} from '@query/shared';
import { getParameter } from './url-params.js';

/**
 * Адрес бэкенда: параметр b= из URL, иначе — собственный origin
 * (перенос `val endpoint = window.location.origin` и логики loadByPost).
 */
export function defaultEndpoint(): string {
  return getParameter(ENDPOINT_SERVICE_URL) ?? window.location.origin;
}

async function postJson(url: string, endpoint: string, parameter: unknown): Promise<Response> {
  return fetch(`${endpoint}/${url}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json; charset=utf-8' },
    body: typeof parameter === 'string' ? parameter : JSON.stringify(parameter),
  });
}

/** POST /loadEnv — карта окружения от сервера. */
export async function loadEnv(endpoint: string): Promise<StartEnv> {
  const response = await postJson(LOADENV, endpoint, {});
  return (await response.json()) as StartEnv;
}

/** POST /bd/passw — проверка админ-пароля (тело — строка без кавычек, как в Ktor-версии). */
export async function checkPassw(url: string, passw: string, endpoint: string | undefined): Promise<boolean> {
  const response = await postJson(url, endpoint ?? defaultEndpoint(), passw);
  return (await response.json()) as boolean;
}

/**
 * Загрузка массива строк — перенос fetchArray (AppFCFun.kt:441-459).
 *
 * Абсолютный URL берётся как есть: стартовый запрос на живом эталоне указывает на
 * внешний json (ag-grid olympic-winners), и такая ветка должна работать.
 */
export async function fetchArray<T>(
  urlIn: string,
  fieldsValue: string,
  endpointServiceUrl: string | undefined,
): Promise<T[]> {
  const base =
    urlIn.startsWith(HTTP) || !endpointServiceUrl ? urlIn : `${endpointServiceUrl}/${urlIn}`;
  const url = `${base}${base.includes('?') ? '&' : '?'}${ROW_FIELDS_VALUE_PARAM}=${
    fieldsValue === '' ? '{}' : fieldsValue
  }`;

  const response = await fetch(url);
  const parsed: unknown = await response.json();
  return (Array.isArray(parsed) ? parsed : []) as T[];
}

/** Заголовок с токеном администратора — требуется для записи (фаза 6). */
export const ADMIN_TOKEN_HEADER = 'X-Admin-Token';

/** Сохранение строк — перенос saveRow (ApiQuerys.kt:22-33). */
export async function saveRow(
  rows: unknown[],
  newValue: Record<string, string>,
  url: string,
  endpoint: string | undefined,
  fieldsValue: Record<string, string> = {},
  adminToken = '',
): Promise<string> {
  const payload: UpdateData = {
    rowsUpd: selectRowByType(rows, newValue, 'upd'),
    rowsDel: selectRowByType(rows, newValue, 'del'),
    rowsIns: selectRowByType(rows, newValue, 'ins,copy'),
    fieldsValue,
  };

  const target = `${url.replace(JSON_PG, JSON_PG_UPD)}`;
  const full = target.startsWith(HTTP) ? target : `${endpoint ?? defaultEndpoint()}/${target}`;
  const response = await fetch(full, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      ...(adminToken === '' ? {} : { [ADMIN_TOKEN_HEADER]: adminToken }),
    },
    body: JSON.stringify(payload),
  });
  return response.text();
}

/** Отбор строк по типу изменения — перенос selectRowByType (ApiQuerys.kt:35-40). */
export function selectRowByType(
  rows: unknown[],
  newValue: Record<string, string>,
  type: string,
): string[] {
  const types = type.split(',');
  return Object.entries(newValue)
    .filter(([, value]) => types.includes(value))
    .map(([index]) => rows[Number(index)])
    .filter((row) => row !== undefined)
    .map((row) => JSON.stringify(row));
}

/** Карта значений полей выделенной строки из query-параметра rFV. */
export function parseRowFieldsValue(raw: string | undefined): Record<string, string> {
  if (raw === undefined || raw === '') return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).map(([key, value]) => [key, String(value)]),
    );
  } catch {
    return {};
  }
}

/** Проверка ответа на признак ошибки — как `contains(ERROR_FIELD)` в оригинале. */
export function hasError(value: unknown): boolean {
  return JSON.stringify(value ?? '').includes(ERROR_FIELD);
}

/** Признак записи из таблицы querys (её `_sql` — маска вместо строки подключения). */
export function isQueryRecordArray(value: unknown): value is QueryRecord[] {
  return Array.isArray(value) && value.every((row) => typeof row === 'object' && row !== null);
}
